import React, { useState, useEffect, useRef } from 'react';
import { useClass } from '../utils/ClassContext';
import ClassWorkspace from './ClassWorkspace';
import LoginModal from './LandingPage/LoginModal';

const PublicClassView = ({ classId, apiBaseUrl, onLogin }) => {
  const { changeClass, selectedClass, isPublicView, publicClassId } = useClass();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const hasFetchedRef = useRef(false);

  // Fetch public class details - only once
  useEffect(() => {
    // Skip if we already have this class loaded
    if (hasFetchedRef.current || (selectedClass && publicClassId === classId && isPublicView)) {
      if (selectedClass && !classData) {
        setClassData(selectedClass);
        setLoading(false);
      }
      return;
    }

    const fetchPublicClass = async () => {
      try {
        setLoading(true);
        hasFetchedRef.current = true;
        const response = await fetch(`${apiBaseUrl}/public/classes/${classId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.class) {
            setClassData(data.class);
            // Set class in context for ClassWorkspace
            changeClass(data.class, true, classId);
          } else {
            setError('Public class not found');
          }
        } else {
          setError('Failed to load public class');
        }
      } catch (err) {
        setError(err.message || 'Failed to load public class');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, apiBaseUrl]); // Removed changeClass from dependencies

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLogin = async (credentials) => {
    try {
      await onLogin(credentials);
      setShowLoginModal(false);
      // After login, user will be redirected to authenticated view
      window.location.reload();
    } catch (error) {
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading public class...</p>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Class Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This class is not available for public viewing.'}</p>
          <a
            href="/"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1">
        <ClassWorkspace
          username=""
          userRole="Public"
          userName={{ firstName: '', lastName: '' }}
          apiKey={null}
          feedbackCount={0}
          quotaValue={0}
          notificationCount={0}
          hasConsent={false}
          updateApiKey={() => {}}
          refreshUserStats={() => {}}
          onNavigate={(page) => {
            // Handle navigation - for public view, limit to view-only pages
            console.log('Public view navigation:', page);
          }}
          apiBaseUrl={apiBaseUrl}
          hasInstructorPermissions={false}
          navigationData={null}
          notificationRefreshRef={null}
          triggerAllUIUpdates={() => {}}
          onConsentSubmitted={() => {}}
          fetchClassNotificationCount={() => Promise.resolve(0)}
          decrementNotificationCount={() => {}}
          rollbackNotificationCount={() => {}}
        />
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
      />
    </div>
  );
};

export default PublicClassView;

